import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  ShoppingBag,
  Check,
  Shield,
  Lock,
  RefreshCw,
  CreditCard,
  Mail,
  X,
  Receipt
} from 'lucide-react';
import {
  getFilteredProductCards,
  getCategoryTheme
} from '../config/productUtils';
import { getProductMainImage } from '../config/productImages';
import LoginModal from '../components/LoginModal';
import { submitGuestOrder, checkOrderStatus } from '../api/productApi';
import { autoLoginWithEmail } from '../store/userSlice';

const AI_FLEET_PRODUCT_TYPES = [
  'aimonkey_vip',
  'aimonkey_vip_legacy',
  'fleet_gpt_plus',
  'fleet_claude',
  'fleet_grok',
  'fleet_gemini'
];

const PRODUCT_TABS = [
  {
    id: 'vip',
    label: 'VIP大会员',
    productTypes: ['aimonkey_vip', 'aimonkey_vip_legacy']
  },
  {
    id: 'gpt',
    label: 'GPT',
    productTypes: ['fleet_gpt_plus']
  },
  {
    id: 'grok',
    label: 'Grok',
    productTypes: ['fleet_grok']
  },
  {
    id: 'claude',
    label: 'Claude',
    productTypes: ['fleet_claude']
  },
  {
    id: 'gemini',
    label: 'Gemini',
    productTypes: ['fleet_gemini']
  }
];

const PRODUCT_CARD_DESCRIPTIONS = {
  aimonkey_vip: '购买后可使用 AI车队中的 GPT Plus/Business号、Claude Max号、Gemini Pro号和 Grok Super号。',
  aimonkey_vip_legacy: '购买后可使用 AI车队中的 GPT Plus/Business号、Gemini Pro号和 Grok Super号。',
  fleet_gpt_plus: '购买后可使用 AI车队中的 GPT Plus/Business号。',
  fleet_claude: '适合长文写作、总结归纳、翻译润色和代码理解场景。',
  fleet_grok: '购买后可使用 AI车队中的 Grok Super号。',
  fleet_gemini: '购买后可使用 AI车队中的 Gemini Pro号。'
};

const PERIOD_TABS = [
  {
    id: 'all',
    label: '全部',
    description: '默认展示全部周期，可按需筛选合适的套餐',
    days: null
  },
  {
    id: 'monthly',
    label: '月度',
    description: '适合短期使用，灵活续费',
    days: 30
  },
  {
    id: 'quarterly',
    label: '季度',
    description: '更适合持续使用，价格与灵活性更均衡',
    days: 90
  },
  {
    id: 'yearly',
    label: '年度',
    description: '最优惠价格，长期稳定使用',
    days: 365
  }
];

const isProductInPeriod = (product, periodId) => {
  if (periodId === 'all') return true;
  if (periodId === 'monthly') return product.month === 1;
  if (periodId === 'quarterly') return product.month === 3;
  if (periodId === 'yearly') return product.month === 12 || product.month > 3;
  return false;
};

const isProductInTab = (product, tabId) => {
  const currentTab = PRODUCT_TABS.find((tab) => tab.id === tabId);
  if (!currentTab) return false;
  return currentTab.productTypes.includes(product.productCategoryId);
};

const getProductCardDescription = (product) => {
  return product.productDescription || PRODUCT_CARD_DESCRIPTIONS[product.productCategoryId] || '查看该套餐详情并选择适合您的使用周期。';
};

const ShopCheckout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 获取登录状态和用户信息
  const { isLoggedIn, user } = useSelector((state) => state.user);

  // 状态管理
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('alipay');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [showManualRedirect, setShowManualRedirect] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [showPaymentMethodAlert, setShowPaymentMethodAlert] = useState(false);
  const [emailError, setEmailError] = useState('');

  // 周期Tab状态
  const [selectedSubTab, setSelectedSubTab] = useState('all'); // 全部/月度/季度/年度
  const [selectedProductTab, setSelectedProductTab] = useState('vip');

  // 获取所有产品（memoize 避免每次渲染创建新引用导致下游 useMemo 失效）
  const allProducts = useMemo(() => getFilteredProductCards('all'), []);

  const eligibleAiFleetProducts = useMemo(() => {
    // 固定筛选 AI 车队服务
    let filteredProducts = allProducts.filter(p =>
      AI_FLEET_PRODUCT_TYPES.includes(p.productCategoryId)
    );

    // 过滤老版VIP：仅对老用户（注册时间 < 2025-12-08）和已登录用户显示
    filteredProducts = filteredProducts.filter(p => {
      // 如果不是老版VIP，直接显示
      if (p.productCategoryId !== 'aimonkey_vip_legacy') {
        return true;
      }

      // 如果是老版VIP，需要判断用户资格
      // 未登录用户：不显示老版VIP
      if (!isLoggedIn || !user) {
        return false;
      }

      // 已登录用户：检查注册时间
      try {
        const userCreatedAt = new Date(user.created_at);
        const cutoffDate = new Date('2025-12-08');

        // 老用户（注册时间 < 2025-12-08）：显示老版VIP
        return userCreatedAt < cutoffDate;
      } catch (error) {
        console.error('解析用户注册时间失败:', error);
        // 如果解析失败，默认不显示老版VIP
        return false;
      }
    });

    // 产品排序：VIP → GPT → Grok → Claude → Gemini
    const sortOrder = {
      'fleet_gpt_plus': 1,
      'fleet_grok': 2,
      'fleet_claude': 3,
      'fleet_gemini': 4,
      'aimonkey_vip': 0,  // VIP排最前
      'aimonkey_vip_legacy': 0  // VIP排最前
    };

    filteredProducts.sort((a, b) => {
      const orderA = sortOrder[a.productCategoryId] ?? 999;
      const orderB = sortOrder[b.productCategoryId] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      // 同类产品按月份降序排列（年卡→季卡→月卡）
      return (b.month || 0) - (a.month || 0);
    });

    return filteredProducts;
  }, [allProducts, isLoggedIn, user]);

  const periodProducts = useMemo(() => {
    return eligibleAiFleetProducts.filter(product => isProductInPeriod(product, selectedSubTab));
  }, [eligibleAiFleetProducts, selectedSubTab]);

  // 获取当前应该显示的产品
  const currentProducts = useMemo(() => {
    if (selectedProductTab === 'claude') {
      return [];
    }
    return periodProducts.filter(product => isProductInTab(product, selectedProductTab));
  }, [periodProducts, selectedProductTab]);

  const activePeriodTab = PERIOD_TABS.find(tab => tab.id === selectedSubTab) || PERIOD_TABS[0];

  const gptPricingHint = useMemo(() => {
    const exchangeRate = 6.9;
    const gptQuarterlyProduct = eligibleAiFleetProducts.find((product) => (
      product.productCategoryId === 'fleet_gpt_plus' && product.month === 3
    ));
    const gptYearlyProduct = eligibleAiFleetProducts.find((product) => (
      product.productCategoryId === 'fleet_gpt_plus' && product.month === 12
    ));

    if (!gptQuarterlyProduct || !gptYearlyProduct) {
      return '';
    }

    const quarterlyPerDay = ((gptQuarterlyProduct.price * exchangeRate) / 90).toFixed(2);
    const yearlyPerDay = ((gptYearlyProduct.price * exchangeRate) / 365).toFixed(2);

    return `GPT 季卡、年卡按 $1=¥${exchangeRate} 估算，折合约 ¥${quarterlyPerDay}/天、¥${yearlyPerDay}/天，长期使用低至每天约 1 元。`;
  }, [eligibleAiFleetProducts]);

  const showClaudeNotice = selectedProductTab === 'claude';

  // 当前周期下如果所选产品tab无商品，则自动切换到第一个可用tab
  useEffect(() => {
    const hasProductsInCurrentTab = periodProducts.some(product => isProductInTab(product, selectedProductTab));

    if (hasProductsInCurrentTab) {
      return;
    }

    const firstAvailableTab = PRODUCT_TABS.find(tab =>
      periodProducts.some(product => tab.productTypes.includes(product.productCategoryId))
    );

    if (firstAvailableTab && firstAvailableTab.id !== selectedProductTab) {
      setSelectedProductTab(firstAvailableTab.id);
    }
  }, [periodProducts, selectedProductTab]);

  // 默认选择当前周期下的第一个产品
  useEffect(() => {
    setSelectedProduct((prevSelectedProduct) => {
      if (currentProducts.length === 0) {
        return null;
      }

      const isCurrentSelectionVisible = prevSelectedProduct
        ? currentProducts.some(product => product.id === prevSelectedProduct.id)
        : false;

      if (isCurrentSelectionVisible) {
        return prevSelectedProduct;
      }

      return currentProducts[0];
    });
  }, [currentProducts]);

  // 支付方式配置
  const paymentMethods = {
    all: [
      { id: 'alipay', name: '支付宝', icon: '/images/payment/16.jpg', description: '国内用户首选', channel: 1 },
      { id: 'alipay_hk', name: '支付宝香港', icon: '/images/payment/17.jpg', description: '支持国际支付', channel: 2 },
      { id: 'credit_card', name: '信用卡', icon: '/images/payment/18.jpg', description: 'Visa/Mastercard', channel: 10 }
    ]
  };

  // 用户登录后自动填充邮箱
  useEffect(() => {
    if (isLoggedIn && user) {
      const email = user.uid || user.email || '';
      setCustomerEmail(email);
    }
  }, [isLoggedIn, user]);

  // 收货信息验证（支持邮箱或手机号，只验证非空）
  const validateCustomerInfo = (info) => {
    return info && info.trim().length > 0;
  };

  // 处理产品选择（支持取消选择）
  const handleSelectProduct = (product) => {
    // 如果点击的是已选中的产品，则取消选择
    if (selectedProduct?.id === product.id) {
      setSelectedProduct(null);
    } else {
      setSelectedProduct(product);
    }

    // 滚动到支付面板（移动端）
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        document.getElementById('checkout-panel')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // 处理支付
  const handlePayment = async () => {
    // 验证产品选择
    if (!selectedProduct) {
      alert('请先选择产品');
      return;
    }

    // 验证收货信息（可以是邮箱或手机号）
    if (!customerEmail) {
      setEmailError('请输入收货信息（邮箱或手机号）');
      return;
    }
    if (!validateCustomerInfo(customerEmail)) {
      setEmailError('收货信息不能为空');
      return;
    }
    setEmailError('');

    // 验证支付方式
    if (!selectedPaymentMethod) {
      setShowPaymentMethodAlert(true);
      setTimeout(() => setShowPaymentMethodAlert(false), 3000);
      return;
    }

    setPaymentLoading(true);

    try {
      // 获取选中的支付方式详情
      const selectedPaymentDetails = paymentMethods.all.find(
        method => method.id === selectedPaymentMethod
      );

      if (!selectedPaymentDetails) {
        throw new Error('未找到支付方式');
      }

      // 准备支付请求数据
      const paymentData = {
        uid: customerEmail,
        product_id: selectedProduct.id,
        channel: selectedPaymentDetails.channel,
        domain: window.location.hostname,
        amount: selectedProduct.price,
        quantity: 1,
        version: selectedProduct.label || selectedProduct.key,
        productName: selectedProduct.productName,
        productType: selectedProduct.productCategoryId
      };

      console.log('支付请求数据:', paymentData);

      // 调用支付接口
      const response = await submitGuestOrder(paymentData);

      console.log('支付API返回:', response);

      if (response && response.data) {
        const { payUrl, out_trade_no } = response.data;

        if (payUrl) {
          setPaymentUrl(payUrl);
          setOrderNumber(out_trade_no);

          // 打开支付页面
          window.open(payUrl, '_blank');

          // 显示手动跳转按钮
          setShowManualRedirect(true);
        } else {
          throw new Error('支付链接为空');
        }
      } else {
        throw new Error('支付接口返回数据异常');
      }

    } catch (error) {
      console.error('支付失败:', error);
      alert(`支付失败: ${error.message}`);
    } finally {
      setPaymentLoading(false);
    }
  };

  // 订单状态轮询
  const startPolling = async (orderNo) => {
    setIsPolling(true);
    console.log('开始轮询订单状态，订单号:', orderNo);

    try {
      const response = await checkOrderStatus(orderNo);
      console.log('订单状态查询结果:', response);

      if (response && response.data) {
        const { pay_result } = response.data;

        if (pay_result === 'success') {
          // 支付成功
          console.log('🎉 支付完成！');
          setIsPolling(false);
          clearTimeout(pollingIntervalRef.current);

          // 如果用户未登录且有邮箱，先进行自动登录
          if (!isLoggedIn && !autoLoginAttempted && customerEmail) {
            console.log('💡 检测到未登录用户，使用订单邮箱进行自动登录:', customerEmail);
            setAutoLoginAttempted(true);

            try {
              const result = await dispatch(autoLoginWithEmail(customerEmail));

              if (autoLoginWithEmail.fulfilled.match(result)) {
                console.log('✅ 自动登录成功:', result.payload);
                setTimeout(() => {
                  navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
                }, 500);
              } else {
                console.log('❌ 自动登录失败，直接跳转');
                navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
              }
            } catch (error) {
              console.error('💥 自动登录过程中发生错误，直接跳转:', error);
              navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
            }
          } else {
            console.log('🔄 用户已登录或已尝试过自动登录，直接跳转到成功页面');
            navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
          }
          return;
        } else {
          // 支付未完成，继续轮询
          console.log('支付未完成，继续轮询。当前状态:', pay_result);
          pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
        }
      } else {
        console.log('订单状态查询返回数据异常，继续轮询');
        pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
      }
    } catch (error) {
      console.error('支付状态轮询失败:', error);
      pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
    }
  };

  // 当有订单号和支付链接时启动轮询
  useEffect(() => {
    if (orderNumber && paymentUrl && !isPolling) {
      console.log('启动订单状态轮询');
      startPolling(orderNumber);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, [orderNumber, paymentUrl]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-red-500">
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 rounded-full p-3 animate-float">
                <ShoppingBag className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              选择套餐，立即购买
            </h1>
            <p className="text-white/90 text-lg">
              选择适合您的套餐，完成支付即可开始使用
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* 左侧 - 产品列表 (占3/5) */}
          <div className="lg:col-span-3">

            {/* 产品类型 Tab */}
            <div className="mb-8">
              <h3 className="text-base font-bold text-gray-900 mb-3">
                选择产品
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {PRODUCT_TABS.map((tab) => {
                  const isActive = selectedProductTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        if (tab.id !== selectedProductTab) {
                          setSelectedProductTab(tab.id);
                          setSelectedProduct(null);
                        }
                      }}
                      className={`
                        whitespace-nowrap px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200
                        ${isActive
                          ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 周期选择 */}
            <div className="mb-6">
              <h3 className="text-base font-bold text-gray-900 mb-3">
                选择套餐周期
              </h3>

              <div className="flex gap-2 overflow-x-auto pb-1">
                {PERIOD_TABS.map((tab) => {
                  const isActive = selectedSubTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setSelectedSubTab(tab.id)}
                      className={`
                        whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200
                        ${isActive
                          ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      {isActive && <Check className="w-3.5 h-3.5" />}
                      <span>{tab.label}</span>
                      {tab.days && (
                        <>
                          <span className={isActive ? 'text-white/70' : 'text-gray-400'}>·</span>
                          <span className={isActive ? 'text-white/80' : 'text-gray-500'}>
                            {tab.days}天
                          </span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 px-1 space-y-2">
                <p className="text-sm text-gray-500">
                  {activePeriodTab.description}
                </p>
                {selectedProductTab === 'gpt' && gptPricingHint && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-sm leading-6 text-amber-700">
                      {gptPricingHint}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Claude 单独停售公告 */}
            {showClaudeNotice ? (
              <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center text-xl flex-shrink-0">
                    公告
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 mb-3">
                      Claude 服务暂不支持单独购买
                    </h4>
                    <p className="text-sm text-gray-600 leading-7 mb-4">
                      由于 Claude 当前存在并发限制，且封号后的售后补偿已难以维持固定退款，整体成本持续上升。
                      为保障现有 Claude 用户的使用体验，Claude 服务现已不再单独出售；如需使用该服务，请购买 VIP大会员套餐。
                    </p>
                    <div className="mb-5 rounded-2xl border border-amber-200 bg-white px-4 py-3">
                      <p className="text-sm text-amber-800 leading-6">
                        VIP大会员购买后可使用 AI车队中的 GPT Plus/Business号、Claude Max号、Gemini Pro号和 Grok Super号。
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedProductTab('vip')}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-all duration-200"
                    >
                      前往购买 VIP大会员
                      <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {currentProducts.map((product, index) => {
                  // VIP大会员（新版）使用特殊卡片设计
                  if (product.productCategoryId === 'aimonkey_vip') {
                    return (
                      <VIPProductCard
                        key={`${product.productCategoryId}-${product.id}`}
                        product={product}
                        index={index}
                        isSelected={selectedProduct?.id === product.id}
                        onSelect={() => handleSelectProduct(product)}
                      />
                    );
                  }
                  // VIP大会员（经典版/老版）使用经典卡片设计
                  if (product.productCategoryId === 'aimonkey_vip_legacy') {
                    return (
                      <VIPLegacyProductCard
                        key={`${product.productCategoryId}-${product.id}`}
                        product={product}
                        index={index}
                        isSelected={selectedProduct?.id === product.id}
                        onSelect={() => handleSelectProduct(product)}
                      />
                    );
                  }
                  // 其他产品使用标准卡片
                  return (
                    <ProductCard
                      key={`${product.productCategoryId}-${product.id}`}
                      product={product}
                      index={index}
                      isSelected={selectedProduct?.id === product.id}
                      onSelect={() => handleSelectProduct(product)}
                    />
                  );
                })}
              </div>
            )}

            {/* 如果当前分类没有产品 - 联系客服/即将推出提示 */}
            {currentProducts.length === 0 && !showClaudeNotice && (
              <div className="py-16 text-center">
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-100 via-amber-100 to-yellow-100 rounded-3xl flex items-center justify-center shadow-xl border-2 border-purple-200">
                  <span className="text-6xl animate-bounce">🚀</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">暂无可购买套餐</h3>
                <p className="text-gray-500 text-base mb-6 max-w-md mx-auto">
                  当前分类下暂无可购买套餐，请切换上方产品标签或套餐周期查看
                </p>
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-50 to-amber-50 rounded-xl border-2 border-purple-200">
                  <span className="text-purple-600 font-semibold">💡 提示</span>
                  <span className="text-purple-700 text-sm">您可以切换 VIP、GPT、Grok、Claude 或 Gemini 查看</span>
                </div>
              </div>
            )}

          </div>

          {/* 右侧 - 支付面板 (占2/5) */}
          <div className="lg:col-span-2">
            <div id="checkout-panel" className="lg:sticky lg:top-24">
              <CheckoutPanel
                selectedProduct={selectedProduct}
                customerEmail={customerEmail}
                setCustomerEmail={setCustomerEmail}
                emailError={emailError}
                setEmailError={setEmailError}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                paymentMethods={paymentMethods}
                paymentLoading={paymentLoading}
                handlePayment={handlePayment}
                showPaymentMethodAlert={showPaymentMethodAlert}
                setShowPaymentMethodAlert={setShowPaymentMethodAlert}
              />
            </div>
          </div>

        </div>
      </div>

      {/* 手动跳转提醒框 */}
      {showManualRedirect && paymentUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform relative">
            <button
              onClick={() => setShowManualRedirect(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 group"
            >
              <X className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-purple-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                💳 支付页面已打开
              </h3>

              <p className="text-gray-600 text-sm mb-6">
                如果支付页面没有自动打开，请点击下方按钮手动打开
              </p>

              {isPolling && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-blue-700 text-sm font-medium">
                      正在检测支付状态...
                    </span>
                  </div>
                  <p className="text-blue-600 text-xs mt-1 text-center">
                    支付完成后将自动跳转
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = paymentUrl}
                  className="w-full bg-gradient-to-r from-purple-500 to-red-500 text-white py-4 rounded-xl font-bold hover:from-purple-600 hover:to-red-600 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg transform hover:scale-105"
                >
                  <Receipt className="w-5 h-5" />
                  手动打开支付页面
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 登录弹窗 */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
};

// VIP大会员专属产品卡片组件 - 简约尊贵设计
const VIPProductCard = ({ product, index, isSelected, onSelect }) => {
  // 4个车队的配置信息（不含 Sora）
  const fleets = [
    { name: 'GPT Plus', icon: '🤖', image: '/images/gpt-main.jpg', color: 'from-gray-500 to-gray-600' },
    { name: 'Claude', icon: '🧠', image: '/images/claude-main.jpg', color: 'from-purple-500 to-purple-600' },
    { name: 'Grok', icon: '🚀', image: '/images/grok-main.jpg', color: 'from-purple-500 to-purple-600' },
    { name: 'Gemini', icon: '✨', image: '/images/gemini-main.jpg', color: 'from-green-500 to-emerald-600' }
  ];
  const productDescription = getProductCardDescription(product);

  return (
    <div
      onClick={onSelect}
      className={`
        relative bg-gradient-to-br from-amber-50 via-yellow-50 to-purple-50 rounded-2xl overflow-hidden
        shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer
        border-2 ${isSelected ? 'border-purple-500 ring-4 ring-purple-200' : 'border-yellow-400'}
        transform hover:scale-[1.02] hover:-translate-y-1
        ${product.stock > 0 ? '' : 'opacity-60 cursor-not-allowed'}
        group
      `}
      style={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
        boxShadow: '0 4px 20px rgba(251, 191, 36, 0.3)' // 金色阴影
      }}
    >
      {/* 金色光泽动画效果 - 增强版 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40"
        style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer 2.5s ease-in-out infinite',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 第二层光泽 - 增加深度 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-100 to-transparent opacity-30"
        style={{
          backgroundSize: '150% 100%',
          animation: 'shimmer 3s ease-in-out infinite 0.5s',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 鼠标悬停时的额外反光效果 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-300"
        style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer-fast 1.5s ease-in-out infinite',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 套餐周期标签 - 左上角 */}
      {product.duration && (
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-md text-xs font-bold text-white">
            {product.duration}
          </span>
        </div>
      )}

      {/* 选中标记 */}
      {isSelected && (
        <div className="absolute top-3 right-3 z-10">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
            <Check className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      <div className="p-5 relative z-10">
        {/* VIP标识 - 紧凑设计 */}
        <div className="mb-3 text-center">
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 rounded-lg shadow-md">
            <span className="text-lg">👑</span>
            <span className="font-bold text-white text-sm tracking-wide">VIP大会员</span>
          </div>
        </div>

        {/* 4个车队图标 - 横向紧凑排列 */}
        <div className="mb-4 flex justify-center gap-1">
          {fleets.map((fleet, idx) => (
            <div key={idx} className="relative">
              <div className={`w-10 h-10 bg-gradient-to-br ${fleet.color} rounded-lg overflow-hidden shadow-sm border border-white/50`}>
                <img
                  src={fleet.image}
                  alt={fleet.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full hidden items-center justify-center text-sm">
                  {fleet.icon}
                </div>
              </div>
              {/* 小对勾 */}
              <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border border-white">
                <Check className="w-2 h-2 text-white" />
              </div>
            </div>
          ))}
        </div>

        {/* 产品描述 */}
        <div className="text-center mb-4">
          <div className="min-h-[44px] px-3 py-2 bg-white/70 rounded-xl border border-white/80">
            <p className="text-xs text-gray-600 leading-5">
              {productDescription}
            </p>
          </div>
        </div>

        {/* 价格 - 单行显示 */}
        <div className="text-center">
          <div className="inline-flex items-baseline gap-2">
            <span
              className="text-3xl font-black bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent"
              style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            >
              ${product.price}
            </span>
            {product.priceRMB && (
              <span className="text-sm font-semibold text-gray-600">
                ≈ ¥{product.priceRMB}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// VIP大会员（经典版）专属产品卡片组件 - 老会员专享，3个车队，银色主题
const VIPLegacyProductCard = ({ product, index, isSelected, onSelect }) => {
  // 3个车队的配置信息（不含 Sora / Claude，包含 Gemini）
  const fleets = [
    { name: 'GPT Plus', icon: '🤖', image: '/images/gpt-main.jpg', color: 'from-gray-500 to-gray-600' },
    { name: 'Grok', icon: '🚀', image: '/images/grok-main.jpg', color: 'from-purple-500 to-purple-600' },
    { name: 'Gemini', icon: '✨', image: '/images/gemini-main.jpg', color: 'from-green-500 to-emerald-600' }
  ];
  const productDescription = getProductCardDescription(product);

  return (
    <div
      onClick={onSelect}
      className={`
        relative bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 rounded-2xl overflow-hidden
        shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer
        border-2 ${isSelected ? 'border-purple-500 ring-4 ring-purple-200' : 'border-purple-300'}
        transform hover:scale-[1.02] hover:-translate-y-1
        ${product.stock > 0 ? '' : 'opacity-60 cursor-not-allowed'}
        group
      `}
      style={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
        boxShadow: '0 4px 20px rgba(167, 139, 250, 0.3)' // 紫色阴影
      }}
    >
      {/* 紫色光泽动画效果 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
        style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer 2.5s ease-in-out infinite',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 第二层光泽 - 紫色 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-100 to-transparent opacity-20"
        style={{
          backgroundSize: '150% 100%',
          animation: 'shimmer 3s ease-in-out infinite 0.5s',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 鼠标悬停时的额外反光效果 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"
        style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer-fast 1.5s ease-in-out infinite',
          transform: 'skewX(-20deg)'
        }}
      ></div>

      {/* 套餐周期标签 - 左上角 */}
      {product.duration && (
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg shadow-md text-xs font-bold text-white">
            {product.duration}
          </span>
        </div>
      )}

      {/* 选中标记 */}
      {isSelected && (
        <div className="absolute top-3 right-3 z-10">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
            <Check className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      <div className="p-5 relative z-10">
        {/* VIP标识 + 老会员专享标签 - 紫色主题 */}
        <div className="mb-3 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-500 via-violet-600 to-purple-500 rounded-lg shadow-md">
            <span className="text-lg">👑</span>
            <span className="font-bold text-white text-sm tracking-wide">VIP大会员（老用户专享）</span>
          </div>
        </div>

        {/* 3个车队图标 - 横向紧凑排列 */}
        <div className="mb-4 flex justify-center gap-1.5">
          {fleets.map((fleet, idx) => (
            <div key={idx} className="relative">
              <div className={`w-11 h-11 bg-gradient-to-br ${fleet.color} rounded-lg overflow-hidden shadow-sm border border-white/50`}>
                <img
                  src={fleet.image}
                  alt={fleet.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full hidden items-center justify-center text-sm">
                  {fleet.icon}
                </div>
              </div>
              {/* 小对勾 */}
              <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border border-white">
                <Check className="w-2 h-2 text-white" />
              </div>
            </div>
          ))}
        </div>

        {/* 老会员版本说明 */}
        <div className="mb-3 text-center">
          <div className="px-3 py-2 bg-red-50 rounded-xl border border-red-200 text-left">
            <p className="text-xs leading-5 text-red-700">
              因 Claude 当前并发与售后成本持续上升，为保障相关服务的整体体验，
              老会员专享优惠套餐现已调整为不含 Claude，并补充加入 Gemini。
              购买后可使用 GPT Plus/Business、Grok Super 和 Gemini Pro。感谢老用户一直以来的理解与支持，
              我们也会继续以更实惠的价格，为大家提供稳定服务。
            </p>
          </div>
        </div>

        {/* 产品描述 */}
        <div className="text-center mb-4">
          <div className="min-h-[44px] px-3 py-2 bg-white/70 rounded-xl border border-white/80">
            <p className="text-xs text-gray-600 leading-5">
              {productDescription}
            </p>
          </div>
        </div>

        {/* 价格 - 单行显示 */}
        <div className="text-center">
          <div className="inline-flex items-baseline gap-2">
            <span
              className="text-3xl font-black bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent"
              style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            >
              ${product.price}
            </span>
            {product.priceRMB && (
              <span className="text-sm font-semibold text-gray-600">
                ≈ ¥{product.priceRMB}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 产品卡片组件
const ProductCard = ({ product, index, isSelected, onSelect }) => {
  const theme = getCategoryTheme(product.productCategoryId);
  // 优先使用产品数据中的图片，确保车队产品显示正确的图片
  const productImage = product.productImage || getProductMainImage(product.productCategoryId);
  const isGift = product.isGift || false;
  const productDescription = getProductCardDescription(product);

  return (
    <div
      onClick={isGift ? null : onSelect}  // 赠品不可选择
      className={`
        relative bg-gradient-to-br ${theme.gradient} rounded-2xl overflow-hidden
        shadow-lg hover:shadow-2xl transition-all duration-300
        border-2 ${isSelected ? 'border-purple-500 ring-4 ring-purple-200' : theme.border}
        transform hover:scale-[1.02] hover:-translate-y-1
        ${product.stock > 0 && !isGift ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
      `}
      style={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`
      }}
    >
      {/* 套餐周期标签 - 左上角 */}
      {!isGift && product.duration && (
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg shadow-md text-xs font-bold text-white">
            {product.duration}
          </span>
        </div>
      )}

      {/* 赠品标签 */}
      {isGift && (
        <div className="absolute top-3 left-3 z-10">
          <div className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-red-500 rounded-lg shadow-lg">
            <span className="text-white text-xs font-bold">🎁 {product.giftLabel}</span>
          </div>
        </div>
      )}

      {/* 选中标记（非赠品） */}
      {isSelected && !isGift && (
        <div className="absolute top-3 right-3 z-10">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
            <Check className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      <div className="p-5">
        {/* 产品图片 */}
        <div className="mb-4 flex justify-center">
          <div className="w-16 h-16 bg-white rounded-xl overflow-hidden shadow-md">
            <img
              src={productImage}
              alt={product.productName}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* 产品信息 */}
        <div className="text-center mb-4">
          <h4 className="font-bold text-gray-900 mb-2 text-base">
            {product.productName}
          </h4>
          <div className="min-h-[44px] px-3 py-2 bg-white/80 rounded-xl border border-white/90">
            <p className="text-xs text-gray-600 leading-5">
              {productDescription}
            </p>
          </div>
        </div>

        {/* 价格或赠品提示 */}
        <div className="text-center">
          {isGift ? (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-100 to-amber-100 rounded-lg border border-purple-300">
              <span className="text-sm font-bold text-purple-700">免费赠送</span>
            </div>
          ) : (
            <div className="inline-flex items-baseline gap-2">
              <span
                className="text-3xl font-black bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent"
                style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              >
                ${product.price}
              </span>
              {product.priceRMB && (
                <span className="text-sm font-semibold text-gray-600">
                  ≈ ¥{product.priceRMB}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 右侧支付面板组件
const CheckoutPanel = ({
  selectedProduct,
  customerEmail,
  setCustomerEmail,
  emailError,
  setEmailError,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  paymentMethods,
  paymentLoading,
  handlePayment,
  showPaymentMethodAlert,
  setShowPaymentMethodAlert
}) => {
  const total = selectedProduct ? selectedProduct.price : 0;

  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden">
      <div className="p-6 space-y-5">

        {/* 标题 */}
        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-red-500 rounded-xl flex items-center justify-center">
            <Receipt className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">确认订单</h2>
        </div>

        {/* 选中的产品 */}
        {selectedProduct ? (
          <div className="p-4 bg-gradient-to-br from-purple-50 to-amber-50 rounded-2xl border border-purple-200">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white rounded-lg overflow-hidden shadow-sm flex-shrink-0">
                <img
                  src={getProductMainImage(selectedProduct.productCategoryId)}
                  alt={selectedProduct.productName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-gray-900 text-sm mb-1">
                  {selectedProduct.productName}
                </h4>
                <p className="text-purple-600 text-xs font-semibold">
                  {selectedProduct.label || selectedProduct.key}
                </p>
                {/* 套餐信息 */}
                {selectedProduct.month > 0 && (
                  <div className="mt-1 text-xs text-gray-600">
                    📅 {selectedProduct.month}个月套餐 · {selectedProduct.duration || `${selectedProduct.month}个月`}
                  </div>
                )}
                <div className="mt-2 flex items-baseline gap-2">
                  <span
                    className="text-2xl font-black bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent"
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                  >
                    ${selectedProduct.price}
                  </span>
                  {selectedProduct.priceRMB && (
                    <span className="text-sm font-semibold text-gray-600">
                      ≈ ¥{selectedProduct.priceRMB}
                    </span>
                  )}
                </div>
                {/* 钻石奖励 */}
                {selectedProduct.diamondReward > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-yellow-100 to-purple-100 rounded-lg border border-purple-300">
                    <span className="text-xs">🎁</span>
                    <span className="text-xs font-bold text-purple-700">
                      +{selectedProduct.diamondReward} 钻石
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-400">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">请先选择左侧产品</p>
          </div>
        )}

        {/* 收货信息（只读，显示用户名） */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-500" />
            收货信息
          </label>
          <input
            type="text"
            value={customerEmail}
            readOnly
            placeholder="请先登录"
            className={`w-full px-4 py-3 border-2 ${
              emailError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            } rounded-xl text-gray-700 cursor-not-allowed`}
          />
          {emailError ? (
            <p className="mt-1 text-xs text-red-500">{emailError}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">使用您的账号信息（邮箱或手机号）</p>
          )}
        </div>

        {/* 支付方式选择 */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            选择支付渠道
          </label>
          <div className="space-y-2">
            {paymentMethods.all.map((method) => (
              <button
                key={method.id}
                onClick={() => setSelectedPaymentMethod(method.id)}
                className={`w-full p-3 rounded-xl text-left transition-all duration-200 flex items-center justify-between ${
                  selectedPaymentMethod === method.id
                    ? 'border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-amber-50 shadow-md'
                    : 'border border-gray-200 hover:border-purple-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex-shrink-0">
                    <img
                      src={method.icon}
                      alt={method.name}
                      className="w-full h-full object-contain rounded"
                    />
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">{method.name}</span>
                </div>
                {selectedPaymentMethod === method.id && (
                  <div className="w-5 h-5 bg-gradient-to-r from-purple-500 to-red-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 总计 */}
        <div className="p-4 bg-gradient-to-r from-purple-100 to-amber-100 rounded-2xl border border-purple-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-900">总计</span>
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-red-500 bg-clip-text text-transparent">
              ${total.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 支付按钮 */}
        <button
          onClick={handlePayment}
          disabled={paymentLoading || !selectedProduct}
          className={`w-full py-4 rounded-2xl font-bold text-lg shadow-2xl flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden group relative ${
            !paymentLoading && selectedProduct
              ? 'bg-gradient-to-r from-purple-500 via-purple-600 to-red-500 text-white hover:scale-105 transform'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {/* 动态背景效果 */}
          {!paymentLoading && selectedProduct && (
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-red-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          )}

          {/* 按钮内容 */}
          <div className="relative z-10 flex items-center gap-3">
            {paymentLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>处理中...</span>
              </>
            ) : (
              <>
                <CreditCard className="w-6 h-6" />
                <span>立即支付 ${total.toFixed(2)}</span>
                <span className="text-2xl">→</span>
              </>
            )}
          </div>
        </button>

        {/* 支付方式选择提醒 */}
        {showPaymentMethodAlert && (
          <div className="p-3 bg-gradient-to-r from-red-50 to-purple-50 border-2 border-red-200 rounded-xl animate-pulse">
            <div className="flex items-center gap-2">
              <span className="text-red-500 text-lg">⚠️</span>
              <div className="flex-1">
                <p className="font-bold text-red-800 text-xs">
                  请选择支付方式
                </p>
              </div>
              <button
                onClick={() => setShowPaymentMethodAlert(false)}
                className="w-5 h-5 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3 text-red-500" />
              </button>
            </div>
          </div>
        )}

        {/* 安全提示 */}
        <div className="flex items-center justify-center gap-3 text-xs text-gray-600 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-green-500" />
            <span>安全支付</span>
          </div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="flex items-center gap-1">
            <Lock className="w-3 h-3 text-blue-500" />
            <span>SSL加密</span>
          </div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="flex items-center gap-1">
            <RefreshCw className="w-3 h-3 text-purple-500" />
            <span>质量保障</span>
          </div>
        </div>

        {/* 重要声明 */}
        <div className="p-3 bg-gradient-to-r from-purple-50 to-yellow-50 border-l-4 border-purple-400 rounded-lg text-xs">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
            <div className="text-purple-700 leading-relaxed">
              本产品支持<span className="font-semibold">免费试用</span>，购买即表示接受服务条款，
              <span className="font-semibold">不退不换</span>。
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ShopCheckout;

// 添加动画样式
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-3px); }
    }

    @keyframes shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }

    .animate-float {
      animation: float 3s ease-in-out infinite;
    }

    .animate-shimmer {
      animation: shimmer 3s ease-in-out infinite;
    }
  `;
  if (!document.head.querySelector('style[data-shop-checkout-animations]')) {
    style.setAttribute('data-shop-checkout-animations', 'true');
    document.head.appendChild(style);
  }
}
